import React, { useState, useEffect, useCallback } from 'react';
import { 
  Container, Typography, Paper, Box, Grid, Card, 
  CardContent, CircularProgress, Button, Divider, Chip,
  Tabs, Tab, Stack, Avatar, useTheme, useMediaQuery,
  List, ListItem, ListItemText
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import Plot from 'react-plotly.js';
import { useAuth } from '../context/AuthContext';
import {
  PictureAsPdf as PdfIcon,
  ArrowBack as BackIcon,
  CalendarMonth as CalendarIcon,
  MonetizationOn as BudgetIcon,
  Person as OwnerIcon,
  Assignment as TaskIcon,
  Analytics as AnalyticsIcon,
  Info as InfoIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  TextField, MenuItem, IconButton, Alert
} from '@mui/material';
import { getProjectById } from '../api/projectService';
import { getTasksByProjectId, createTask, updateTask, deleteTask, Task } from '../api/taskService';
import { getTaskStatusChart } from '../api/projectAnalyticsService2';

const ProjectDetailPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [project, setProject] = useState<any>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [taskChart, setTaskChart] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Task Dialog State
  const [openTaskDialog, setOpenTaskDialog] = useState(false);
  const [isEditingTask, setIsEditingTask] = useState(false);
  const [currentTask, setCurrentTask] = useState<Partial<Task>>({});
  const [taskSuccess, setTaskSuccess] = useState('');
  const [taskError, setTaskError] = useState('');

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const id = parseInt(projectId);
      const [projectData, tasksData, taskChartData] = await Promise.all([
        getProjectById(id),
        getTasksByProjectId(id),
        getTaskStatusChart(id)
      ]);
      setProject(projectData);
      setTasks(tasksData);
      setTaskChart(taskChartData);
    } catch (err: any) {
      setError(err.message || 'Failed to load project details');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (isAuthenticated) fetchData();
  }, [fetchData, isAuthenticated]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Task Handlers
  const handleOpenTaskDialog = (task?: Task) => {
    if (task) {
      setCurrentTask({ ...task, due_date: task.due_date ? task.due_date.substring(0, 10) : '' });
      setIsEditingTask(true);
    } else {
      setCurrentTask({ project_id: parseInt(projectId!), title: '', description: '', status: 'To Do', priority: 'Medium', due_date: '' });
      setIsEditingTask(false);
    }
    setOpenTaskDialog(true);
  };

  const handleCloseTaskDialog = () => {
    setOpenTaskDialog(false);
    setCurrentTask({});
    setTaskError('');
  };

  const handleTaskInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentTask({ ...currentTask, [name]: value });
  };

  const handleTaskSubmit = async () => {
    if (!projectId) return;
    try {
      if (isEditingTask && currentTask.id) {
        await updateTask(currentTask.id, currentTask);
        setTaskSuccess('Task updated successfully');
      } else {
        await createTask(currentTask as Task);
        setTaskSuccess('Task created successfully');
      }
      handleCloseTaskDialog();
      fetchData(); // Refresh data
      setTimeout(() => setTaskSuccess(''), 3000);
    } catch (err: any) {
      setTaskError(err.message || 'Failed to save task');
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await deleteTask(taskId);
        setTaskSuccess('Task deleted successfully');
        fetchData();
        setTimeout(() => setTaskSuccess(''), 3000);
      } catch (err: any) {
        setTaskError(err.message || 'Failed to delete task');
      }
    }
  };

  const canEdit = user && ['admin', 'editor'].includes(user.role);

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <CircularProgress size={60} />
      <Typography sx={{ mt: 2 }} color="text.secondary">Loading your project...</Typography>
    </Box>
  );

  if (error) return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 4, border: '1px solid', borderColor: 'error.light' }}>
        <Typography color="error" variant="h5" gutterBottom>Oops!</Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>{error}</Typography>
        <Button variant="contained" onClick={() => navigate('/projects')}>Back to Projects</Button>
      </Paper>
    </Container>
  );

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 6 }}>
        <Button 
          startIcon={<BackIcon />} 
          onClick={() => navigate('/projects')}
          sx={{ mb: 3, color: 'text.secondary', fontWeight: 600 }}
        >
          All Projects
        </Button>
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main', fontWeight: 800, fontSize: '2rem' }}>
              {project?.name.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h3" component="h1" sx={{ fontWeight: 800 }}>{project?.name}</Typography>
              <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
                <Chip label={project?.status} size="small" color="primary" sx={{ fontWeight: 700, px: 1 }} />
                <Chip label={project?.priority} size="small" variant="outlined" sx={{ fontWeight: 700, px: 1 }} />
              </Stack>
            </Box>
          </Box>
          <Button variant="contained" startIcon={<PdfIcon />} size="large" sx={{ borderRadius: 2, px: 4, fontWeight: 800 }}>
            Generate Report
          </Button>
        </Box>
      </Box>

      {taskSuccess && <Alert severity="success" sx={{ mb: 4, borderRadius: 2 }}>{taskSuccess}</Alert>}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant={isMobile ? "fullWidth" : "standard"}>
          <Tab icon={<InfoIcon fontSize="small" />} iconPosition="start" label="Overview" sx={{ fontWeight: 700, py: 2 }} />
          <Tab icon={<TaskIcon fontSize="small" />} iconPosition="start" label="Tasks" sx={{ fontWeight: 700, py: 2 }} />
          <Tab icon={<AnalyticsIcon fontSize="small" />} iconPosition="start" label="Analytics" sx={{ fontWeight: 700, py: 2 }} />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <Grid container spacing={4}>
          <Grid {...{ item: true, xs: 12, md: 8 }}>
            <Paper sx={{ p: 4, borderRadius: 4, height: '100%' }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 800, mb: 3 }}>Project Description</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, fontSize: '1.1rem' }}>
                {project?.description}
              </Typography>
            </Paper>
          </Grid>
          <Grid {...{ item: true, xs: 12, md: 4 }}>
            <Paper sx={{ p: 4, borderRadius: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 800, mb: 3 }}>Quick Details</Typography>
              <List disablePadding>
                {[
                  { label: 'Start Date', value: new Date(project.start_date).toLocaleDateString(), icon: <CalendarIcon color="primary" /> },
                  { label: 'Target End', value: new Date(project.target_end_date).toLocaleDateString(), icon: <CalendarIcon color="primary" /> },
                  { label: 'Project Budget', value: `$${project.budget.toLocaleString()}`, icon: <BudgetIcon color="success" /> },
                  { label: 'Project Lead', value: 'Admin User', icon: <OwnerIcon color="info" /> }
                ].map((item, i) => (
                  <ListItem key={i} disableGutters sx={{ py: 2 }}>
                    <Avatar sx={{ bgcolor: alpha(theme.palette.background.default, 0.5), mr: 2.5, width: 48, height: 48 }}>{item.icon}</Avatar>
                    <ListItemText primary={item.label} secondary={item.value} primaryTypographyProps={{ variant: 'caption', color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase' }} secondaryTypographyProps={{ variant: 'h6', sx: { fontWeight: 700, color: 'text.primary' } }} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabValue === 1 && (
        <Box sx={{ pb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>Project Tasks ({tasks.length})</Typography>
            <Stack direction="row" spacing={2}>
              {canEdit && (
                <Button 
                  variant="contained" 
                  startIcon={<AddIcon />} 
                  onClick={() => handleOpenTaskDialog()}
                  sx={{ borderRadius: 2, fontWeight: 700 }}
                >
                  Add Task
                </Button>
              )}
              <Button variant="outlined" size="medium" onClick={() => navigate(`/projects/${projectId}/tasks`)} sx={{ fontWeight: 700, borderRadius: 2 }}>Manage All Tasks</Button>
            </Stack>
          </Box>
          <Grid container spacing={3}>
            {tasks.map((task) => (
              <Grid {...{ item: true, xs: 12, sm: 6, md: 4 }} key={task.id}>
                <Card sx={{ height: '100%', borderRadius: 4, border: '1px solid', borderColor: 'divider', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }} elevation={0}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'flex-start' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{task.title}</Typography>
                      <Chip label={task.status} size="small" color={task.status === 'Done' ? 'success' : 'primary'} variant="outlined" sx={{ fontWeight: 700 }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, height: 44, overflow: 'hidden', lineHeight: 1.6 }}>{task.description}</Typography>
                    <Divider sx={{ mb: 2.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarIcon fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Due: {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'N/A'}
                        </Typography>
                      </Box>
                      {canEdit && (
                        <Box>
                          <IconButton size="small" onClick={() => handleOpenTaskDialog(task)} color="info">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => task.id && handleDeleteTask(task.id)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {tabValue === 2 && (
        <Grid container spacing={4} sx={{ pb: 4 }}>
          <Grid {...{ item: true, xs: 12, md: 6 }}>
            <Paper sx={{ p: 4, borderRadius: 4, height: '100%' }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 800, mb: 4 }}>Task Distribution</Typography>
              {taskChart && (
                <Plot
                  data={taskChart.data}
                  layout={{ ...taskChart.layout, autosize: true, paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)' }}
                  style={{ width: '100%', height: '400px' }}
                  useResizeHandler={true}
                />
              )}
            </Paper>
          </Grid>
          <Grid {...{ item: true, xs: 12, md: 6 }}>
            <Paper sx={{ p: 4, borderRadius: 4, height: '100%' }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 800, mb: 4 }}>Performance Metrics</Typography>
              <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>Advanced project metrics and health indicators based on real-time task data.</Typography>
              <Stack spacing={5} sx={{ mt: 2 }}>
                {[
                  { label: 'Completion Rate', value: `${project?.completion_percentage || 0}%`, color: theme.palette.success.main },
                  { label: 'Schedule Variance', value: project?.status === 'On Track' ? 'On Track' : project?.status, color: theme.palette.primary.main },
                  { label: 'Risk Exposure', value: project?.priority === 'High' ? 'High' : 'Low', color: theme.palette.info.main }
                ].map((m, i) => (
                  <Box key={i}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="subtitle1" fontWeight={700}>{m.label}</Typography>
                      <Typography variant="subtitle1" fontWeight={800} sx={{ color: m.color }}>{m.value}</Typography>
                    </Box>
                    <Box sx={{ width: '100%', height: 8, bgcolor: alpha(m.color, 0.1), borderRadius: 4 }}>
                      <Box sx={{ width: m.value.includes('%') ? m.value : '100%', height: '100%', bgcolor: m.color, borderRadius: 4 }} />
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Task Dialog */}
      <Dialog open={openTaskDialog} onClose={handleCloseTaskDialog} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1.5rem' }}>
          {isEditingTask ? 'Edit Task' : 'New Project Task'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            {taskError && <Alert severity="error">{taskError}</Alert>}
            <TextField 
              label="Title" 
              name="title" 
              fullWidth 
              value={currentTask.title || ''} 
              onChange={handleTaskInputChange} 
              required 
            />
            <TextField 
              label="Description" 
              name="description" 
              fullWidth 
              multiline 
              rows={3} 
              value={currentTask.description || ''} 
              onChange={handleTaskInputChange} 
            />
            <Grid container spacing={2}>
              <Grid {...{ item: true, xs: 6 }}>
                <TextField 
                  label="Status" 
                  name="status" 
                  select 
                  fullWidth 
                  value={currentTask.status || 'To Do'} 
                  onChange={handleTaskInputChange}
                >
                  {['To Do', 'In Progress', 'Blocked', 'Done'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid {...{ item: true, xs: 6 }}>
                <TextField 
                  label="Priority" 
                  name="priority" 
                  select 
                  fullWidth 
                  value={currentTask.priority || 'Medium'} 
                  onChange={handleTaskInputChange}
                >
                  {['Low', 'Medium', 'High', 'Critical'].map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>
            <TextField 
              label="Due Date" 
              name="due_date" 
              type="date" 
              fullWidth 
              InputLabelProps={{ shrink: true }} 
              value={currentTask.due_date || ''} 
              onChange={handleTaskInputChange} 
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 4 }}>
          <Button onClick={handleCloseTaskDialog} sx={{ fontWeight: 700 }}>Cancel</Button>
          <Button 
            onClick={handleTaskSubmit} 
            variant="contained" 
            size="large" 
            sx={{ borderRadius: 2, px: 4, fontWeight: 800 }}
          >
            Save Task
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

// Helper for alpha color
const alpha = (color: string, value: number) => {
  return `${color}${Math.round(value * 255).toString(16).padStart(2, '0')}`;
};

export default ProjectDetailPage;